import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  StringField,
  TextField,
  TimestampField,
  UuidField,
  IntegerField,
  TimeField,
} from '@ayocore/platform/common/helpers/schema-types';
import taskTypeSchemaDefinition from './task-type.schema';
import taskStatusSchemaDefinition from './task-status.schema';
import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';

import { name as moduleName } from '../module.json';
/**
 * Task Schema - Main task entity with configurable types and statuses
 *
 * Links to any entity (leads, partners, contacts, etc.) using UUID.
 * Uses configurable task types and statuses for flexible workflows.
 */

// Complete schema (with system fields and relations)
const TaskSchema = Type.Object({
  ...SystemFields, // id, uuid, created_at, updated_at

  title: StringField({
    title: 'Название задачи',
    minLength: 1,
    maxLength: 255,
    description: 'Название задачи',
  }),

  description: TextField({
    title: 'Описание',
    nullable: true,
    maxLength: 2000,
    description: 'Подробное описание задачи',
  }),

  // ✨ Relations - TypeScript types and FK fields
  task_type: RelationOne(taskTypeSchemaDefinition), // For TypeScript type
  task_type_id: RelationField(taskTypeSchemaDefinition, 'ID типа задачи', {
    onDelete: 'RESTRICT',
    description: 'ID типа задачи для классификации',
    relationName: 'task_type',
  }),

  task_status: RelationOne(taskStatusSchemaDefinition), // For TypeScript type
  task_status_id: RelationField(taskStatusSchemaDefinition, 'Статус', {
    onDelete: 'RESTRICT',
    description: 'Текущий статус выполнения задачи',
    relationName: 'task_status',
  }),

  assignee: RelationOne(userSchemaDefinition), // For TypeScript type
  assignee_id: RelationField(userSchemaDefinition, 'Отвественный', {
    onDelete: 'SET NULL',
    nullable: true,
    description: 'Пользователь, отвественный за выполнение',
    relationName: 'assignee',
  }),

  // ✨ Tree structure - will be added after schema definition
  parent_task_id: IntegerField({
    title: 'Родительская задача ID',
    nullable: true,
    description: 'Внешний ключ к родительской задаче',
  }),

  due_date: TimestampField({
    title: 'Срок выполнения',
    nullable: true,
    description: 'Когда задача должна быть выполнена',
  }),

  due_time: TimeField({
    title: 'Время выполнения',
    nullable: true,
    description: 'Время в формате ЧЧ:ММ, например, 14:30)',
  }),

  // Поля факта завершения. Заполняются хуком TaskRepository при переходе
  // task_status.is_final: false → true; обнуляются при обратном переходе.
  completed_at: TimestampField({
    title: 'Дата завершения',
    nullable: true,
    description: 'Момент перехода задачи в финальный статус',
  }),

  completed_by: RelationOne(userSchemaDefinition),
  completed_by_id: RelationField(userSchemaDefinition, 'Кем завершена', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Пользователь, переведший задачу в финальный статус',
    relationName: 'completed_by',
  }),

  // Polymorphic link to any CRM entity
  entity_uuid: UuidField({
    title: 'UUID сущности',
    nullable: true,
    description: 'UUID связанной сущности (лид, партнер, контакт, etc.)',
  }),

  entity_type: StringField({
    title: 'Тип сущности',
    maxLength: 50,
    nullable: true,
    description: 'Тип связанной сущности: lead, partner, contact_person, etc.',
  }),
});

// TypeScript types
export type Task = Static<typeof TaskSchema>;

// ✨ CLEAN: Schema definition following our established pattern
export default defineSchema({
  moduleName,
  tableName: 'tasks', // System will prefix with 'tasks_'
  schema: TaskSchema,
  additionalIndexes: [
    'task_type_id', // Fast lookup by type
    'task_status_id', // Fast lookup by status
    'assignee_id', // Fast lookup by assignee
    'parent_task_id', // Fast lookup by parent task
    'due_date', // Sort by due date
    'completed_at', // Filter by relevance (recently completed)
    'entity_uuid', // Fast lookup by related entity
    'entity_type', // Filter by entity type
  ],
  foreignKeys: [
    // ✨ RelationOne fields auto-generate their FK constraints!
    // Only manual/special foreign keys need to be defined here
    {
      column: 'parent_task_id',
      references: { table: 'tasks_tasks', column: 'id' },
      onDelete: 'CASCADE', // Delete child tasks when parent is deleted
    },
  ],
  dependencies: ['tasks_task_types', 'tasks_task_statuses', 'identity_users'], // Need all these first
});
